import React, { useMemo, useState, useRef, useEffect, useImperativeHandle } from "react";
import { createPortal } from "react-dom";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/Card";

/**
 * HighlightText
 * Props:
 * - text: string
 * - highlights: Array<{ start: number, end: number, data?: any }>
 * - renderInfo?: (data) => ReactNode
 * - className?: string
 */
const HighlightText = React.forwardRef(({ text, highlights = [], renderInfo, className = "", scrollBoxClassName = "", extraChips = [], commonRanges = [], omissionsChips = [], omissionRanges = [], hallucinationsChips = [], hallucinationRanges = [], showHighlightChips = true, showCommonChips = true, showOmissionChips = true, showHallucinationChips = true, visibleLayer = 'missing', getMedicationSuggestions }, ref) => {
  const [hoverState, setHoverState] = useState(null); // { idx, data, rect }
  const scrollRef = useRef(null);
  const spanRefs = useRef([]); // refs for highlight spans by normalized index
  const segmentRefs = useRef([]); // refs for all segments in render order
  const [activeChipIdx, setActiveChipIdx] = useState(null);
  const [rawSelected, setRawSelected] = useState(null); // {start,end}
  const rawMidRef = useRef(null);
  const [rawSelectionKind, setRawSelectionKind] = useState(null); // 'common' | 'omission' | 'hallucination' | null
  const hoverHideTimerRef = useRef(null);

  const showHover = (rect, data, idx = -1) => {
    if (hoverHideTimerRef.current) {
      clearTimeout(hoverHideTimerRef.current);
      hoverHideTimerRef.current = null;
    }
    setHoverState({ idx, data, rect });
  };
  const scheduleHideHover = () => {
    if (hoverHideTimerRef.current) clearTimeout(hoverHideTimerRef.current);
    hoverHideTimerRef.current = setTimeout(() => {
      setHoverState(null);
      hoverHideTimerRef.current = null;
    }, 120);
  };

  const safeText = typeof text === "string" ? text : "";
  const heightClass = scrollBoxClassName && scrollBoxClassName.trim().length > 0 ? scrollBoxClassName : "max-h-64";

  const normalized = useMemo(() => {
    const list = Array.isArray(highlights) ? highlights.slice() : [];
    // Clamp and normalize ranges
    const clamped = list
      .map((h, idx) => {
        const start = Math.max(0, Math.min(Number(h.start ?? h.begin ?? h.BeginOffset ?? 0), safeText.length));
        const end = Math.max(start, Math.min(Number(h.end ?? h.EndOffset ?? 0), safeText.length));
        return { start, end, data: h.data ?? h };
      })
      .filter(h => h.end > h.start);
    // Sort by start, then end
    clamped.sort((a, b) => (a.start - b.start) || (a.end - b.end));
    // Remove overlaps by keeping earliest end first
    const merged = [];
    let lastEnd = -1;
    for (const h of clamped) {
      if (h.start >= lastEnd) {
        merged.push(h);
        lastEnd = h.end;
      }
    }
    return merged;
  }, [highlights, safeText.length]);

  const commonNormalized = useMemo(() => {
    const list = Array.isArray(commonRanges) ? commonRanges : [];
    const clamped = list
      .map((r) => {
        const start = Math.max(0, Math.min(Number(r.start ?? r.begin ?? r.TranscriptBeginOffset ?? r.NoteBeginOffset ?? 0), safeText.length));
        const end = Math.max(start, Math.min(Number(r.end ?? r.finish ?? r.TranscriptEndOffset ?? r.NoteEndOffset ?? 0), safeText.length));
        return { start, end, data: r.data };
      })
      .filter(r => r.end > r.start);
    clamped.sort((a, b) => (a.start - b.start) || (a.end - b.end));
    return clamped;
  }, [commonRanges, safeText.length]);

  const omissionNormalized = useMemo(() => {
    const list = Array.isArray(omissionRanges) ? omissionRanges : [];
    const clamped = list
      .map((r) => {
        const start = Math.max(0, Math.min(Number(r.start ?? r.begin ?? r.TranscriptBeginOffset ?? 0), safeText.length));
        const end = Math.max(start, Math.min(Number(r.end ?? r.finish ?? r.TranscriptEndOffset ?? 0), safeText.length));
        return { start, end, data: r.data };
      })
      .filter(r => r.end > r.start);
    clamped.sort((a, b) => (a.start - b.start) || (a.end - b.end));
    return clamped;
  }, [omissionRanges, safeText.length]);

  const hallucinationNormalized = useMemo(() => {
    const list = Array.isArray(hallucinationRanges) ? hallucinationRanges : [];
    const clamped = list
      .map((r) => {
        const start = Math.max(0, Math.min(Number(r.start ?? r.begin ?? r.NoteBeginOffset ?? 0), safeText.length));
        const end = Math.max(start, Math.min(Number(r.end ?? r.finish ?? r.NoteEndOffset ?? 0), safeText.length));
        return { start, end, data: r.data };
      })
      .filter(r => r.end > r.start);
    clamped.sort((a, b) => (a.start - b.start) || (a.end - b.end));
    return clamped;
  }, [hallucinationRanges, safeText.length]);

  const segments = useMemo(() => {
    const parts = [];
    let cursor = 0;
    normalized.forEach((h, idx) => {
      if (cursor < h.start) {
        const text = safeText.slice(cursor, h.start);
        parts.push({ kind: "text", text, startPos: cursor, endPos: h.start });
      }
      const hText = safeText.slice(h.start, h.end);
      parts.push({ kind: "highlight", text: hText, idx, data: h.data, startPos: h.start, endPos: h.end });
      cursor = h.end;
    });
    if (cursor < safeText.length) {
      const text = safeText.slice(cursor);
      parts.push({ kind: "text", text, startPos: cursor, endPos: safeText.length });
    }
    return parts;
  }, [normalized, safeText]);

  const renderTable = (data) => {
    try {
      const entries = Object.entries(data ?? {}).filter(([key]) => {
        const k = String(key).toLowerCase();
        return k !== "beginoffset" && k !== "endoffset" && k !== "scoreentity" && k !== "scoreconcept" && k !== "id";
      });
      return (
        <table className="w-full text-xs text-gray-800">
          <tbody>
            {entries.map(([key, value]) => (
              <tr key={key} className="align-top">
                <td className="py-1 pr-2 font-medium text-gray-600 whitespace-nowrap">{key}</td>
                <td className="py-1">
                  {key === 'MedicationSuggestions' && Array.isArray(value) ? (
                    <div className="whitespace-pre-wrap break-words">
                      {value.slice(0, 5).map((sug, i) => (
                        <div key={i}>{`${sug.name ?? sug.entry ?? ''} (${typeof sug.score === 'number' ? sug.score.toFixed(3) : sug.score ?? ''})`}</div>
                      ))}
                    </div>
                  ) : (
                    typeof value === "object"
                      ? (
                        <pre className="whitespace-pre-wrap break-words">
{JSON.stringify(value, null, 2)}
                        </pre>
                      )
                      : String(value)
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      );
    } catch {
      return (
        <pre className="whitespace-pre-wrap break-words text-xs text-gray-800">
{JSON.stringify(data, null, 2)}
        </pre>
      );
    }
  };

  const onChipClick = (idx) => {
    setActiveChipIdx(idx);
    const container = scrollRef.current;
    const target = spanRefs.current[idx];
    if (!container || !target) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const currentTop = container.scrollTop;
    const targetTopWithin =
      targetRect.top - containerRect.top + currentTop;
    const desired =
      targetTopWithin - (container.clientHeight / 2 - targetRect.height / 2);
    const maxScroll = container.scrollHeight - container.clientHeight;
    const clamped = Math.max(0, Math.min(desired, maxScroll));
    container.scrollTo({ top: clamped, behavior: "smooth" });
  };

  const scrollToIndexInternal = (idx, select) => {
    if (typeof idx !== "number" || idx < 0 || idx >= normalized.length) return;
    if (select) setActiveChipIdx(idx);
    const container = scrollRef.current;
    const target = spanRefs.current[idx];
    if (!container || !target) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    const currentTop = container.scrollTop;
    const targetTopWithin = targetRect.top - containerRect.top + currentTop;
    const desired = targetTopWithin - (container.clientHeight / 2 - targetRect.height / 2);
    const maxScroll = container.scrollHeight - container.clientHeight;
    const clamped = Math.max(0, Math.min(desired, maxScroll));
    container.scrollTo({ top: clamped, behavior: "smooth" });
  };

  const scrollToRawOffsetInternal = (start, select) => {
    if (typeof start !== "number" || segments.length === 0) return;
    let segIdx = segments.findIndex(s => start >= (s.startPos ?? Infinity) && start < (s.endPos ?? -Infinity));
    if (segIdx < 0) {
      let nearest = 0;
      let best = Infinity;
      segments.forEach((s, i) => {
        const d = Math.abs((s.startPos ?? 0) - start);
        if (d < best) { best = d; nearest = i; }
      });
      segIdx = nearest;
    }
    const seg = segments[segIdx];
    if (select && seg && seg.kind === "highlight" && typeof seg.idx === "number") {
      setActiveChipIdx(seg.idx);
    }
    const container = scrollRef.current;
    const targetEl = segmentRefs.current[segIdx];
    if (!container || !targetEl) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = targetEl.getBoundingClientRect();
    const currentTop = container.scrollTop;
    const targetTopWithin = targetRect.top - containerRect.top + currentTop;
    const desired = targetTopWithin - (container.clientHeight / 2 - targetRect.height / 2);
    const maxScroll = container.scrollHeight - container.clientHeight;
    const clamped = Math.max(0, Math.min(desired, maxScroll));
    container.scrollTo({ top: clamped, behavior: "smooth" });
  };

  const scrollElementIntoCenter = (el) => {
    const container = scrollRef.current;
    if (!container || !el) return;
    const containerRect = container.getBoundingClientRect();
    const targetRect = el.getBoundingClientRect();
    const currentTop = container.scrollTop;
    const targetTopWithin = targetRect.top - containerRect.top + currentTop;
    const desired = targetTopWithin - (container.clientHeight / 2 - targetRect.height / 2);
    const maxScroll = container.scrollHeight - container.clientHeight;
    const clamped = Math.max(0, Math.min(desired, maxScroll));
    container.scrollTo({ top: clamped, behavior: "smooth" });
  };

  const buildPopupInfo = (itemData) => {
    if (!itemData) return null;
    const raw = itemData.raw || {};
    const kind = itemData.kind || "entity";
    const pane = itemData.pane || "transcript";
    const startOffset = pane === "note"
      ? (raw.NoteBeginOffset ?? raw.BeginOffset ?? null)
      : (raw.TranscriptBeginOffset ?? raw.BeginOffset ?? null);
    const endOffset = pane === "note"
      ? (raw.NoteEndOffset ?? raw.EndOffset ?? null)
      : (raw.TranscriptEndOffset ?? raw.EndOffset ?? null);

    // Hallucination (note-only) should display Note* fields
    if (kind.includes("hallucination")) {
      if (kind.includes("concept")) {
        const text = raw.NoteText ?? raw.TrancriptText ?? raw.TranscriptText ?? "";
        const info = {
          CommonType: "Concept (Hallucination)",
          NoteText: text,
          NoteConceptCode: raw.NoteConceptCode ?? raw.TranscriptConceptCode ?? raw.ConceptCode ?? "",
          NoteConceptDescription: raw.NoteConceptDescription ?? raw.TranscriptConceptDescription ?? raw.ConceptDescription ?? "",
          NoteBeginOffset: startOffset,
          NoteEndOffset: endOffset,
        };
        return info;
      } else {
        const text = raw.NoteText ?? raw.TrancriptText ?? raw.TranscriptText ?? "";
        const info = {
          CommonType: "Entity (Hallucination)",
          NoteText: text,
          NoteCategory: raw.NoteCategory ?? raw.TranscriptCategory ?? raw.Category ?? "",
          NoteType: raw.NoteType ?? raw.TranscriptType ?? raw.Type ?? "",
          NoteBeginOffset: startOffset,
          NoteEndOffset: endOffset,
        };
        return info;
      }
    }

    if (kind === "concept" || kind === "concept_omission" || kind === "concept_hallucination") {
      // Concept common
      const text = raw.TrancriptText ?? raw.TranscriptText ?? raw.NoteText ?? "";
      const base = {
        CommonType: kind.includes("omission") ? "Concept (Omission)" : kind.includes("hallucination") ? "Concept (Hallucination)" : "Concept",
        TrancriptText: text,
        TranscriptConceptCode: raw.TranscriptConceptCode ?? raw.ConceptCode ?? "",
        TranscriptConceptDescription: raw.TranscriptConceptDescription ?? raw.ConceptDescription ?? "",
      };
      // For non-note panes we keep transcript fields; hallucination-specific offsets handled above
      return base;
    }
    // Entity common
    const text = raw.TrancriptText ?? raw.TranscriptText ?? raw.NoteText ?? "";
    const base = {
      CommonType: kind.includes("omission") ? "Entity (Omission)" : kind.includes("hallucination") ? "Entity (Hallucination)" : "Entity",
      TranscriptText: text,
      TranscriptCategory: raw.TranscriptCategory ?? raw.Category ?? "",
      TranscriptType: raw.TranscriptType ?? raw.Type ?? "",
    };
    return base;
  };

  const getInfoForRange = (a, b) => {
    let match = commonNormalized.find(r => a < r.end && b > r.start && r.data);
    if (!match) match = omissionNormalized.find(r => a < r.end && b > r.start && r.data);
    if (!match) match = hallucinationNormalized.find(r => a < r.end && b > r.start && r.data);
    return buildPopupInfo(match?.data);
  };

  const chipLabelFor = (h, idx) => {
    const raw = h?.data ?? {};
    const label =
      raw.Text ??
      raw.text ??
      safeText.slice(normalized[idx]?.start ?? 0, normalized[idx]?.end ?? 0) ??
      `#${idx + 1}`;
    return String(label).slice(0, 80);
  };

  useEffect(() => {
    if (normalized.length === 0) {
      setActiveChipIdx(null);
    }
  }, [normalized]);

  useImperativeHandle(ref, () => ({
    scrollToIndex: (idx, select = false) => {
      scrollToIndexInternal(idx, !!select);
    },
    scrollToOffset: (start, select = false) => {
      if (typeof start !== "number" || normalized.length === 0) return;
      let idx = normalized.findIndex(h => Math.abs((h?.start ?? -1) - start) < 1e-6);
      if (idx < 0) {
        let nearest = 0;
        let best = Infinity;
        normalized.forEach((h, i) => {
          const d = Math.abs((h?.start ?? 0) - start);
          if (d < best) {
            best = d;
            nearest = i;
          }
        });
        idx = nearest;
      }
      scrollToIndexInternal(idx, !!select);
    },
    scrollToRawOffset: (start, select = false) => {
      scrollToRawOffsetInternal(start, !!select);
    },
    scrollToRawRange: (start, end, select = false, kind = null) => {
      if (typeof start !== "number" || typeof end !== "number" || end <= start) return;
      setRawSelected({ start, end });
      setRawSelectionKind(kind);
      // center on the exact green mid span after render
      setTimeout(() => {
        const anchor = rawMidRef.current;
        scrollElementIntoCenter(anchor);
      }, 0);
      if (select) {
        // also set active orange highlight if range lands on an orange segment
        scrollToRawOffsetInternal(start, true);
      }
    },
    setRawSelection: (start, end) => {
      if (typeof start === "number" && typeof end === "number" && end > start) {
        setRawSelected({ start, end });
      } else {
        setRawSelected(null);
      }
    },
    getHighlights: () => normalized
  }), [normalized, segments]);

  return (
    <div className={`w-full ${className}`}>
      {/* Chips */}
      {showHighlightChips && normalized.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {normalized.map((h, idx) => (
            <button
              key={idx}
              type="button"
              onClick={() => onChipClick(idx)}
              className={`px-2 py-1 text-xs rounded-full text-gray-900 transition-colors ${activeChipIdx === idx ? "bg-gray-500 text-white" : "bg-gray-300 hover:bg-gray-400"}`}
              title="Scroll to highlight"
            >
              {chipLabelFor(h, idx)}
            </button>
          ))}
        </div>
      )}
      {showCommonChips && Array.isArray(extraChips) && extraChips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {extraChips.map((chip, idx) => (
            <button
              key={idx}
              type="button"
              onClick={chip?.onClick}
              className={`px-2 py-1 text-xs rounded-full text-gray-900 transition-colors ${chip?.className || "bg-green-300 hover:bg-green-400"}`}
              title={chip?.title || "Scroll to highlight in both panes"}
            >
              {chip?.label || `Item ${idx + 1}`}
            </button>
          ))}
        </div>
      )}
      {showHallucinationChips && Array.isArray(hallucinationsChips) && hallucinationsChips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {hallucinationsChips.map((chip, idx) => (
            <button
              key={idx}
              type="button"
              onClick={chip?.onClick}
              className={`px-2 py-1 text-xs rounded-full text-gray-900 transition-colors ${chip?.className || "bg-red-300 hover:bg-red-400"}`}
              title={chip?.title || "Scroll to note hallucination"}
            >
              {chip?.label || `Hallucination ${idx + 1}`}
            </button>
          ))}
        </div>
      )}
      {showOmissionChips && Array.isArray(omissionsChips) && omissionsChips.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-2">
          {omissionsChips.map((chip, idx) => (
            <button
              key={idx}
              type="button"
              onClick={chip?.onClick}
              className={`px-2 py-1 text-xs rounded-full text-gray-900 transition-colors ${chip?.className || "bg-red-300 hover:bg-red-400"}`}
              title={chip?.title || "Scroll to transcript omission"}
            >
              {chip?.label || `Omission ${idx + 1}`}
            </button>
          ))}
        </div>
      )}
      <div ref={scrollRef} className={`bg-gray-50 border border-gray-200 rounded p-3 ${heightClass} overflow-y-auto`}>
        <div className="whitespace-pre-wrap text-xs text-gray-800 font-mono">
          {segments.map((seg, i) => {
            const showMed = visibleLayer === 'missing';
            const showCommon = visibleLayer === 'common';
            const showOmission = visibleLayer === 'omission';
            const showHallucination = visibleLayer === 'hallucination';
            if (seg.kind === "text") {
              const sel = rawSelected;
              const cuts = new Set([seg.startPos ?? 0, seg.endPos ?? 0]);
              if (sel) {
                const a = Math.max(sel.start, seg.startPos ?? 0);
                const b = Math.min(sel.end, seg.endPos ?? 0);
                if (b > a) { cuts.add(a); cuts.add(b); }
              }
              commonNormalized.forEach(r => {
                const a = Math.max(r.start, seg.startPos ?? 0);
                const b = Math.min(r.end, seg.endPos ?? 0);
                if (showCommon && b > a) { cuts.add(a); cuts.add(b); }
              });
              omissionNormalized.forEach(r => {
                const a = Math.max(r.start, seg.startPos ?? 0);
                const b = Math.min(r.end, seg.endPos ?? 0);
                if (showOmission && b > a) { cuts.add(a); cuts.add(b); }
              });
              hallucinationNormalized.forEach(r => {
                const a = Math.max(r.start, seg.startPos ?? 0);
                const b = Math.min(r.end, seg.endPos ?? 0);
                if (showHallucination && b > a) { cuts.add(a); cuts.add(b); }
              });
              const points = Array.from(cuts).sort((a,b)=>a-b);
              const parts = [];
              for (let j=0;j<points.length-1;j++) {
                const a = points[j];
                const b = points[j+1];
                const textStart = a - (seg.startPos ?? 0);
                const textEnd = b - (seg.startPos ?? 0);
                const piece = seg.text.slice(textStart, textEnd);
                const isSel = sel && a >= sel.start && b <= sel.end;
                const inCommon = showCommon && commonNormalized.some(r => a < r.end && b > r.start);
                const inOmission = showOmission && omissionNormalized.some(r => a < r.end && b > r.start);
                const inHallucination = showHallucination && hallucinationNormalized.some(r => a < r.end && b > r.start);
                parts.push({ piece, isSel, inCommon, inOmission, inHallucination, a, b });
              }
              return (
                <span key={i} ref={(el) => { if (el) segmentRefs.current[i] = el; }}>
                  {parts.map((p, k) => {
                    const selAllowed = (rawSelectionKind === 'common' && showCommon) || (rawSelectionKind === 'omission' && showOmission) || (rawSelectionKind === 'hallucination' && showHallucination) || (rawSelectionKind == null && showMed);
                    if (p.isSel && selAllowed) {
                      const info = getInfoForRange(p.a, p.b);
                      return (
                        <span
                          key={k}
                          className={`px-0.5 rounded text-black ${rawSelectionKind === 'omission' ? 'bg-red-400 ring-2 ring-red-600' : rawSelectionKind === 'hallucination' ? 'bg-red-400 ring-2 ring-red-600' : 'bg-green-400 ring-2 ring-green-600'}`}
                          ref={(el) => {
                            if (el && sel && p.a <= sel.start && p.b >= sel.end) {
                              rawMidRef.current = el;
                            }
                          }}
                          title="Hover to view details"
                          onMouseEnter={(e) => {
                            if (info) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              showHover(rect, info);
                            }
                          }}
                          onMouseLeave={scheduleHideHover}
                        >{p.piece}</span>
                      );
                    }
                    if (p.inCommon) {
                      // find first matching common range for tooltip info
                      const info = getInfoForRange(p.a, p.b);
                      return (
                        <span
                          key={k}
                          className="px-0.5 rounded bg-green-200"
                          title="Hover to view details"
                          onMouseEnter={(e) => {
                            if (info) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              showHover(rect, info);
                            }
                          }}
                          onMouseLeave={scheduleHideHover}
                        >{p.piece}</span>
                      );
                    }
                    if (p.inOmission) {
                      const info = getInfoForRange(p.a, p.b);
                      return (
                        <span
                          key={k}
                          className="px-0.5 rounded bg-red-200"
                          title="Hover to view details"
                          onMouseEnter={(e) => {
                            if (info) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              showHover(rect, info);
                            }
                          }}
                          onMouseLeave={scheduleHideHover}
                        >{p.piece}</span>
                      );
                    }
                    if (p.inHallucination) {
                      const info = getInfoForRange(p.a, p.b);
                      return (
                        <span
                          key={k}
                          className="px-0.5 rounded bg-red-200"
                          title="Hover to view details"
                          onMouseEnter={(e) => {
                            if (info) {
                              const rect = e.currentTarget.getBoundingClientRect();
                              showHover(rect, info);
                            }
                          }}
                          onMouseLeave={scheduleHideHover}
                        >{p.piece}</span>
                      );
                    }
                    return <span key={k}>{p.piece}</span>;
                  })}
                </span>
              );
            }
            return (
              <span key={i} ref={(el) => { if (el) segmentRefs.current[i] = el; }}>
                {(() => {
                  const baseClass = (() => {
                    const isSelected = activeChipIdx === seg.idx;
                    const isDim = activeChipIdx != null && !isSelected;
                    if (!showMed) return "";
                    return [
                      "px-0.5 rounded cursor-default transition-colors",
                      isSelected
                        ? "bg-gray-500 text-white ring-2 ring-gray-600"
                        : "bg-gray-300 hover:bg-gray-400",
                      isDim ? "opacity-50" : ""
                    ].join(" ");
                  })();
                  const sel = rawSelected;
                  const cuts = new Set([seg.startPos ?? 0, seg.endPos ?? 0]);
                  if (sel) {
                    const a = Math.max(sel.start, seg.startPos ?? 0);
                    const b = Math.min(sel.end, seg.endPos ?? 0);
                    if (b > a) { cuts.add(a); cuts.add(b); }
                  }
                  commonNormalized.forEach(r => {
                    const a = Math.max(r.start, seg.startPos ?? 0);
                    const b = Math.min(r.end, seg.endPos ?? 0);
                    if (showCommon && b > a) { cuts.add(a); cuts.add(b); }
                  });
                  omissionNormalized.forEach(r => {
                    const a = Math.max(r.start, seg.startPos ?? 0);
                    const b = Math.min(r.end, seg.endPos ?? 0);
                    if (showOmission && b > a) { cuts.add(a); cuts.add(b); }
                  });
                  hallucinationNormalized.forEach(r => {
                    const a = Math.max(r.start, seg.startPos ?? 0);
                    const b = Math.min(r.end, seg.endPos ?? 0);
                    if (showHallucination && b > a) { cuts.add(a); cuts.add(b); }
                  });
                  const points = Array.from(cuts).sort((a,b)=>a-b);
                  const parts = [];
                  for (let j=0;j<points.length-1;j++) {
                    const a = points[j];
                    const b = points[j+1];
                    const textStart = a - (seg.startPos ?? 0);
                    const textEnd = b - (seg.startPos ?? 0);
                    const piece = seg.text.slice(textStart, textEnd);
                    const isSel = sel && a >= sel.start && b <= sel.end;
                    const inCommon = showCommon && commonNormalized.some(r => a < r.end && b > r.start);
                    const inOmission = showOmission && omissionNormalized.some(r => a < r.end && b > r.start);
                    const inHallucination = showHallucination && hallucinationNormalized.some(r => a < r.end && b > r.start);
                    parts.push({ piece, isSel, inCommon, inOmission, inHallucination, a, b });
                  }
                  return (
                    <>
                      {parts.map((p,k)=>{
                        const selAllowed = (rawSelectionKind === 'common' && showCommon) || (rawSelectionKind === 'omission' && showOmission) || (rawSelectionKind === 'hallucination' && showHallucination) || (rawSelectionKind == null && showMed);
                        if (p.isSel && selAllowed) {
                          const info = getInfoForRange(p.a, p.b);
                          return (
                            <span key={k}
                              className={`px-0.5 rounded text-black ${rawSelectionKind === 'omission' ? 'bg-red-400 ring-2 ring-red-600' : rawSelectionKind === 'hallucination' ? 'bg-red-400 ring-2 ring-red-600' : 'bg-green-400 ring-2 ring-green-600'}`}
                              title="Hover to view details"
                              onMouseEnter={(e) => {
                                if (info) { const rect = e.currentTarget.getBoundingClientRect(); showHover(rect, info); }
                              }}
                              onMouseLeave={scheduleHideHover}
                              ref={(el)=>{
                                if (el && sel && p.a <= sel.start && p.b >= sel.end) {
                                  rawMidRef.current = el;
                                }
                              }}
                            >{p.piece}</span>
                          );
                        }
                        if (p.inCommon) {
                          const info = getInfoForRange(p.a, p.b);
                          return (
                            <span key={k}
                              className="px-0.5 rounded bg-green-200"
                              title="Hover to view details"
                              onMouseEnter={(e) => {
                                if (info) { const rect = e.currentTarget.getBoundingClientRect(); showHover(rect, info); }
                              }}
                              onMouseLeave={scheduleHideHover}
                            >{p.piece}</span>
                          );
                        }
                        if (p.inOmission) {
                          const info = getInfoForRange(p.a, p.b);
                          return (
                            <span key={k}
                              className="px-0.5 rounded bg-red-200"
                              title="Hover to view details"
                              onMouseEnter={(e) => {
                                if (info) { const rect = e.currentTarget.getBoundingClientRect(); showHover(rect, info); }
                              }}
                              onMouseLeave={scheduleHideHover}
                            >{p.piece}</span>
                          );
                        }
                        if (p.inHallucination) {
                          const info = getInfoForRange(p.a, p.b);
                          return (
                            <span key={k}
                              className="px-0.5 rounded bg-red-200"
                              title="Hover to view details"
                              onMouseEnter={(e) => {
                                if (info) { const rect = e.currentTarget.getBoundingClientRect(); showHover(rect, info); }
                              }}
                              onMouseLeave={scheduleHideHover}
                            >{p.piece}</span>
                          );
                        }
                        return (
                          <span key={k}
                            className={baseClass}
                            title="Hover to view details"
                            onMouseEnter={(e) => {
                              const rect = e.currentTarget.getBoundingClientRect();
                              let data = seg.data;
                              try {
                                if (typeof getMedicationSuggestions === 'function') {
                                  const suggestions = getMedicationSuggestions(seg.text);
                                  if (suggestions && suggestions.length) {
                                    data = { ...seg.data, MedicationSuggestions: suggestions };
                                  }
                                }
                              } catch (err) { /* ignore suggestion errors */ }
                              showHover(rect, data, seg.idx);
                            }}
                            onMouseLeave={scheduleHideHover}
                            ref={(el) => {
                              if (el) { spanRefs.current[seg.idx] = el; }
                            }}
                          >{p.piece}</span>
                        );
                      })}
                    </>
                  );
                })()}
              </span>
            );
          })}
        </div>
      </div>
      {hoverState &&
        createPortal(
          (() => {
            const margin = 8;
            const cardWidth = 384; // w-96
            const left = Math.min(
              Math.max(0, hoverState.rect.left),
              Math.max(0, window.innerWidth - cardWidth - margin)
            );
            const top = Math.min(
              window.innerHeight - 200, // rough clamp to keep on screen
              hoverState.rect.bottom + margin
            );
            return (
              <div
                style={{ position: "fixed", left, top, zIndex: 9999 }}
                onMouseEnter={() => { if (hoverHideTimerRef.current) { clearTimeout(hoverHideTimerRef.current); hoverHideTimerRef.current = null; } }}
                onMouseLeave={() => setHoverState(null)}
              >
                <Card className="w-96 shadow-lg bg-white border border-gray-200">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm">Entity details</CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    {typeof renderInfo === "function"
                      ? renderInfo(hoverState.data)
                      : renderTable(hoverState.data)}
                  </CardContent>
                </Card>
              </div>
            );
          })(),
          document.body
        )}
    </div>
  );
});

export default HighlightText;


